/**
 * Hebrew & RTL Language Search Enhancement
 * Handles 417 errors from Shopify Predictive Search API for Hebrew/RTL languages
 * Falls back to regular search endpoint when predictive search is unsupported
 */

(function() {
  'use strict';

  // Configuration
  const RTL_LANGUAGES = ['he', 'ar', 'fa', 'ur', 'yi'];
  const FALLBACK_ENABLED = true;
  const MIN_SEARCH_LENGTH = 2;

  // Intercept fetch to handle 417 errors from predictive search API
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const [url, options] = args;

    // Check if this is a predictive search request
    if (typeof url === 'string' && url.includes('/search/suggest')) {
      console.log('[Hebrew Search] Intercepting predictive search request:', url);

      return originalFetch.apply(this, args)
        .then(response => {
          // If we get a 417 error (Unsupported buyer locale), fallback to regular search
          if (response.status === 417) {
            console.log('[Hebrew Search] Got 417 error, falling back to regular search');

            // Extract the search query from the URL
            const urlObj = new URL(url, window.location.origin);
            const query = urlObj.searchParams.get('q');
            const sectionId = urlObj.searchParams.get('section_id');

            if (query) {
              // Use regular search endpoint with section rendering
              // Request both the full search page and product-only search
              const fallbackUrl = `/search?q=${encodeURIComponent(query)}&type=product`;
              console.log('[Hebrew Search] Fetching from fallback URL:', fallbackUrl);

              return originalFetch(fallbackUrl, options)
                .then(fallbackResponse => {
                  if (fallbackResponse.ok) {
                    // Return the HTML response - the theme will parse it
                    return fallbackResponse.clone().text().then(html => {
                      // Parse products from the HTML
                      const products = extractProductsFromHTML(html);

                      if (products.length === 0) {
                        console.warn('[Hebrew Search] No products found in search results');
                        // Return empty results in the expected format
                        return new Response(html, {
                          status: 200,
                          statusText: 'OK',
                          headers: fallbackResponse.headers
                        });
                      }

                      console.log('[Hebrew Search] Converted', products.length, 'products');

                      // Build predictive search HTML response
                      const predictiveSearchHTML = buildPredictiveSearchHTML(products, query);

                      return new Response(predictiveSearchHTML, {
                        status: 200,
                        statusText: 'OK',
                        headers: {
                          'Content-Type': 'text/html'
                        }
                      });
                    });
                  }
                  return fallbackResponse;
                });
            }
          }

          return response;
        });
    }

    return originalFetch.apply(this, args);
  };

  /**
   * Extract product data from search results HTML
   */
  function extractProductsFromHTML(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Try different selectors to find product elements
    const selectors = [
      '[data-product-item]',
      '[data-product-grid-item]',
      '.productitem',
      '.product-item',
      '.product-card',
      '[class*="product"][class*="grid"]',
      '[class*="grid-product"]',
      'article[data-product]',
      '.grid__item a[href*="/products/"]'
    ];

    let productElements = [];
    for (const selector of selectors) {
      productElements = doc.querySelectorAll(selector);
      if (productElements.length > 0) {
        console.log('[Hebrew Search] Found', productElements.length, 'elements using selector:', selector);
        break;
      }
    }

    if (productElements.length === 0) {
      console.warn('[Hebrew Search] No product elements found. Dumping HTML structure...');
      // Log some diagnostic info
      const body = doc.body;
      console.log('[Hebrew Search] Body classes:', body ? body.className : 'no body');
      const mainContent = doc.querySelector('main, #main, .main, [role="main"]');
      console.log('[Hebrew Search] Main content:', mainContent ? mainContent.className : 'not found');

      // Try to find ANY links to products
      const productLinks = doc.querySelectorAll('a[href*="/products/"]');
      console.log('[Hebrew Search] Found', productLinks.length, 'product links total');

      if (productLinks.length > 0) {
        // Use the product links as our elements
        productElements = Array.from(productLinks).slice(0, 10);
        console.log('[Hebrew Search] Using product links as fallback');
      }
    }

    const products = Array.from(productElements).slice(0, 10).map((el, index) => {
      // Try multiple selectors for title
      const titleEl = el.querySelector('[data-product-title], .productitem--title, .product-title, h2, h3, h4, .title');
      // Find the product link
      let linkEl = el.querySelector('a[href*="/products/"]');
      if (!linkEl && el.tagName === 'A' && el.href.includes('/products/')) {
        linkEl = el;
      }
      if (!linkEl) {
        linkEl = el.closest('a[href*="/products/"]');
      }

      // Find price - get clean money value only
      const priceEl = el.querySelector('[data-product-price], .productitem--price, .product-price, .money, [class*="price"]');
      // Find image
      const imageEl = el.querySelector('img, [data-product-image]');

      const title = titleEl ? titleEl.textContent.trim() : (linkEl ? linkEl.getAttribute('aria-label') || linkEl.textContent.trim() : '');
      const url = linkEl ? linkEl.getAttribute('href') : '';
      
      // Extract only the money value from price, removing labels
      let price = '';
      if (priceEl) {
        // Try to find a non-empty .money element for cleaner price
        // Prefer [data-price] as it usually contains the current price
        const allMoneyEls = priceEl.querySelectorAll('.money, [data-price]');
        for (const moneyEl of allMoneyEls) {
          const moneyText = moneyEl.textContent.trim();
          if (moneyText && moneyText.length > 0) {
            price = moneyText;
            break; // Use the first non-empty money element
          }
        }
        
        // If no .money element found with content, try regex on the full text
        if (!price) {
          const priceText = priceEl.textContent;
          // Match currency patterns like "49.90 ₪" or "₪ 49.90"
          const priceMatch = priceText.match(/[\d,.]+\s*₪|₪\s*[\d,.]+|[\d,.]+\s*ILS|ILS\s*[\d,.]+/);
          if (priceMatch) {
            price = priceMatch[0].trim();
          } else {
            // Fallback: just get numbers with currency symbol
            const numMatch = priceText.match(/[\d,.]+/);
            if (numMatch) {
              price = numMatch[0] + ' ₪';
            }
          }
        }
      }
      const imageSrc = imageEl ? (imageEl.getAttribute('src') || imageEl.getAttribute('data-src') || imageEl.getAttribute('data-srcset')?.split(' ')[0]) : '';

      const product = {
        title,
        url,
        price,
        image: imageSrc,
        handle: url ? url.split('/products/')[1]?.split('?')[0] : ''
      };

      if (index === 0) {
        console.log('[Hebrew Search] Sample product:', product);
      }

      return product;
    }).filter(product => product.title && product.url); // Filter out invalid products

    return products;
  }

  /**
   * Build predictive search HTML from product data
   */
  function buildPredictiveSearchHTML(products, query) {
    // Check if we're in RTL mode
    const isRTL = document.documentElement.getAttribute('dir') === 'rtl';
    const rtlLanguages = ['he', 'ar', 'fa', 'ur', 'yi'];
    const currentLang = document.documentElement.getAttribute('lang');
    const shouldBeRTL = isRTL || rtlLanguages.includes(currentLang);

    const productItemsHTML = products.map(product => `
      <li>
        <a class="search-flydown--product" href="${product.url}" aria-label="${product.title}">
          <figure class="search-flydown--product-image">
            ${product.image ? `<img src="${product.image}" alt="${product.title}" loading="lazy">` : '<div class="placeholder--image"></div>'}
          </figure>
          <div class="search-flydown--product-text">
            <span class="search-flydown--product-title">${product.title}</span>
            ${product.price ? `<span class="search-flydown--product-price">${product.price}</span>` : ''}
          </div>
        </a>
      </li>
    `).join('');

    const productsTitle = shouldBeRTL ? 'מוצרים' : 'Products';
    const viewAllText = shouldBeRTL ? `הצג את כל התוצאות עבור "${query}"` : `View all results for "${query}"`;

    return `
      <div class="predictive-search-results" dir="${shouldBeRTL ? 'rtl' : 'ltr'}">
        <div class="search-flydown__products-pages-posts">
          <div class="search-flydown--product-items">
            <h3 class="search-flydown__group-title">${productsTitle}</h3>
            <ul class="search-flydown__group-list">
              ${productItemsHTML}
            </ul>
          </div>
        </div>
        <button class="search-flydown__continue" type="submit">
          ${viewAllText}
        </button>
      </div>
    `;
  }

  // Utility Functions
  const Utils = {
    /**
     * Detect if text contains Hebrew characters
     */
    isHebrewText: function(text) {
      const hebrewRegex = /[\u0590-\u05FF]/;
      return hebrewRegex.test(text);
    },

    /**
     * Detect if text contains Arabic characters
     */
    isArabicText: function(text) {
      const arabicRegex = /[\u0600-\u06FF]/;
      return arabicRegex.test(text);
    },

    /**
     * Detect if text contains RTL characters
     */
    isRTLText: function(text) {
      return this.isHebrewText(text) || this.isArabicText(text);
    },

    /**
     * Get current locale/language
     */
    getCurrentLocale: function() {
      const htmlLang = document.documentElement.getAttribute('lang');
      return htmlLang || 'en';
    },

    /**
     * Check if current locale is RTL
     */
    isRTLLocale: function() {
      const locale = this.getCurrentLocale();
      return RTL_LANGUAGES.includes(locale);
    },

    /**
     * Normalize Hebrew text for better search results
     * Removes diacritics (nikud) and final form letters
     */
    normalizeHebrewText: function(text) {
      if (!text) return '';

      // Remove Hebrew diacritics (nikud) - Unicode range \u0591-\u05C7
      let normalized = text.replace(/[\u0591-\u05C7]/g, '');

      // Map final forms to regular forms
      const finalFormMap = {
        'ך': 'כ',
        'ם': 'מ',
        'ן': 'נ',
        'ף': 'פ',
        'ץ': 'צ'
      };

      normalized = normalized.split('').map(char => finalFormMap[char] || char).join('');

      return normalized.trim();
    },

    /**
     * Debounce function
     */
    debounce: function(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }
  };

  // Search Enhancement Class
  const HebrewSearchEnhancement = {
    initialized: false,
    searchInputs: [],
    originalPredictiveSearchHandler: null,

    /**
     * Initialize the enhancement
     */
    init: function() {
      if (this.initialized) return;

      console.log('[Hebrew Search] Initializing Hebrew search enhancement...');

      // Find all search inputs
      this.searchInputs = document.querySelectorAll('input[type="search"], input[name="q"]');

      if (this.searchInputs.length === 0) {
        console.log('[Hebrew Search] No search inputs found, waiting for DOM...');
        // Wait for DOM to be ready
        document.addEventListener('DOMContentLoaded', () => {
          this.findAndEnhanceSearchInputs();
        });
      } else {
        this.enhanceSearchInputs();
      }

      this.initialized = true;
    },

    /**
     * Find and enhance search inputs after DOM is ready
     */
    findAndEnhanceSearchInputs: function() {
      this.searchInputs = document.querySelectorAll('input[type="search"], input[name="q"]');
      console.log('[Hebrew Search] Found ' + this.searchInputs.length + ' search input(s)');
      this.enhanceSearchInputs();
    },

    /**
     * Enhance all search inputs
     */
    enhanceSearchInputs: function() {
      this.searchInputs.forEach((input) => {
        this.enhanceInput(input);
      });
    },

    /**
     * Enhance a single search input
     */
    enhanceInput: function(input) {
      if (input.dataset.hebrewEnhanced) return;

      console.log('[Hebrew Search] Enhancing search input:', input);

      // Mark as enhanced
      input.dataset.hebrewEnhanced = 'true';

      // Add RTL detection on input
      input.addEventListener('input', Utils.debounce((e) => {
        const value = e.target.value;

        // Auto-detect RTL text and set direction
        if (Utils.isRTLText(value)) {
          e.target.setAttribute('dir', 'rtl');
          e.target.style.textAlign = 'right';
        } else {
          e.target.setAttribute('dir', 'ltr');
          e.target.style.textAlign = 'left';
        }

        // If it's Hebrew text, normalize it for better search
        if (Utils.isHebrewText(value)) {
          const normalized = Utils.normalizeHebrewText(value);
          console.log('[Hebrew Search] Normalized:', value, '->', normalized);
        }
      }, 100));

      // Add placeholder RTL support
      if (Utils.isRTLLocale()) {
        input.setAttribute('dir', 'rtl');
        input.style.textAlign = 'right';
      }
    },

    /**
     * Enhance predictive search results
     */
    enhancePredictiveSearchResults: function(resultsContainer) {
      if (!resultsContainer) return;

      // Set RTL direction if needed
      if (Utils.isRTLLocale()) {
        resultsContainer.setAttribute('dir', 'rtl');
        resultsContainer.style.textAlign = 'right';
      }
    },

    /**
     * Fallback search handler
     * This is called when the native predictive search doesn't return results
     */
    fallbackSearch: function(query, callback) {
      if (!FALLBACK_ENABLED || query.length < MIN_SEARCH_LENGTH) {
        return;
      }

      console.log('[Hebrew Search] Attempting fallback search for:', query);

      // Normalize the query if it's Hebrew
      const normalizedQuery = Utils.isHebrewText(query)
        ? Utils.normalizeHebrewText(query)
        : query;

      // Use Shopify's search API with the normalized query
      const searchUrl = window.Theme && window.Theme.routes && window.Theme.routes.search_url
        ? window.Theme.routes.search_url + '?q=' + encodeURIComponent(normalizedQuery) + '&type=product'
        : '/search?q=' + encodeURIComponent(normalizedQuery) + '&type=product';

      fetch(searchUrl, {
        headers: {
          'Accept': 'application/json'
        }
      })
      .then(response => response.text())
      .then(html => {
        console.log('[Hebrew Search] Fallback search completed');
        if (callback) callback(html);
      })
      .catch(error => {
        console.error('[Hebrew Search] Fallback search error:', error);
      });
    }
  };

  // Observer to watch for dynamically added search inputs
  const searchObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) { // Element node
          // Check if the node itself is a search input
          if (node.matches && node.matches('input[type="search"], input[name="q"]')) {
            HebrewSearchEnhancement.enhanceInput(node);
          }
          // Check if the node contains search inputs
          const inputs = node.querySelectorAll && node.querySelectorAll('input[type="search"], input[name="q"]');
          if (inputs && inputs.length > 0) {
            inputs.forEach(input => HebrewSearchEnhancement.enhanceInput(input));
          }
        }
      });
    });
  });

  // Start observing
  if (document.body) {
    searchObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      HebrewSearchEnhancement.init();
    });
  } else {
    HebrewSearchEnhancement.init();
  }

  // Also try to initialize immediately in case DOM is already ready
  setTimeout(() => {
    HebrewSearchEnhancement.init();
  }, 500);

  // Expose to window for debugging
  window.HebrewSearchEnhancement = HebrewSearchEnhancement;
  window.RTLSearchUtils = Utils;

  console.log('[Hebrew Search] Script loaded. Current locale:', Utils.getCurrentLocale(), 'Is RTL:', Utils.isRTLLocale());

})();